import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./ArticleWithKeywords.css";
import SearchDrawer from "../components/SearchDrawer";
import { dummyArticles } from "../pages/Home";
import GeminiChat from "./GeminiChat";
import TextSelectionPopover from "./textSelectionPopover";

const ArticleWithKeywords = ({ article }) => {
  const [processedContent, setProcessedContent] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(""); // Add this line

  // drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerQuery, setDrawerQuery] = useState("");

  const navigate = useNavigate();

  // ref
  const chatRef = useRef();

  //summary widget functionality
  const [summary] = useState("");




  // Function to get related articles (excluding current one)
  const getRelatedArticles = () => {
    if (!article) return [];

    // Filter out current article and get 4 random related ones
    const otherArticles = dummyArticles.filter((a) => a.id !== article.id);
    const shuffled = otherArticles.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  };



  // Extract & highlight keywords
  useEffect(() => {
    if (article) extractAndHighlightKeywords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const extractAndHighlightKeywords = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch('/api/extract-keywords',
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            articleContent: article.content,
            articleTitle: article.title,
          }),
        }
      );



      if (!response.ok) throw new Error(`API call failed: ${response.status}`);

      const responseData = await response.json();
      console.log('[ArticleWithKeywords] API response:', responseData);
      const extracted = responseData.keywords ?? [];
      console.log('[ArticleWithKeywords] keywords count:', extracted.length, '| first 3:', extracted.slice(0, 3));
      setKeywords(extracted);

      const highlighted = highlightKeywords(article.content, extracted);
      console.log('[ArticleWithKeywords] content changed:', highlighted !== article.content);
      setProcessedContent(highlighted);
    } catch (err) {
      setError(err.message);
      setProcessedContent(article.content);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clean up or simple event listeners can go here if needed
    return () => {
    };
  }, []);

  const highlightKeywords = (content, list) => {
    let out = content;
    list
      .sort((a, b) => b.length - a.length)
      .forEach((kw) => {
        if (!kw || kw.length < 3) return;
        const safe = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${safe}\\b`, "gi");
        out = out.replace(
          regex,
          (m) =>
            `<span class="tiktok-keyword" data-keyword="${encodeURIComponent(
              m
            )}">${m}</span>`
        );
      });
    return out;
  };

  // Enhanced drawer management
  const openDrawerWithQuery = (q) => {
    setDrawerQuery(q);
    setDrawerOpen(true);
  };

  const handleDrawerClose = (shouldReopen = true) => {
    if (shouldReopen === false) {
      // This is a reopen action from the edge button
      setDrawerOpen(true);
    } else {
      // Normal close
      setDrawerOpen(false);
    }
  };

  const handleKeywordClick = (kw) => {
    if (drawerOpen) {
      // If drawer is already open, just update the query
      setDrawerQuery(kw);
    } else {
      // If drawer is closed, open it with the new query
      openDrawerWithQuery(kw);
    }
  };

  // Attach click handlers to keyword spans
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      const kw = decodeURIComponent(
        e.target.getAttribute("data-keyword") || ""
      );
      if (kw) handleKeywordClick(kw);
    };

    document
      .querySelectorAll(".tiktok-keyword")
      .forEach((el) => el.addEventListener("click", handler));

    return () =>
      document
        .querySelectorAll(".tiktok-keyword")
        .forEach((el) => el.removeEventListener("click", handler));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedContent, drawerOpen]);

  // Loading state
  if (loading) {
    return (
      <div className="article-loading">
        <div className="loading-spinner" />
        <p>Processing article keywords…</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: 20, color: "red" }}>
        <h3>Error loading article:</h3>
        <p>{error}</p>
        <h4>Fallback – Original Article:</h4>
        <h1>{article.title}</h1>
        <p>{article.content}</p>
      </div>
    );
  }



  return (
    <>
      <div className="article-with-keywords">
        {/* Article Header */}
        <div className="article-header">
          <h1 className="article-title">{article.title}</h1>
          <div className="article-meta">
            <span>
              {article.date} by {article.author} | {article.source}
            </span>
          </div>
        </div>

        {/* Article Image */}
        {article.image && (
          <img
            src={article.image}
            alt={article.title}
            className="article-image"
          />
        )}

        {/* Article Content with Highlighted Keywords */}
        <div
          className="article-content"
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />

        {/* highlight popover over the article content */}
        <TextSelectionPopover
          onAskLima={(text) => {
            chatRef.current?.askLima(text);
          }}
          onSummarize={(text) => {
            chatRef.current?.summarizeText(text);
          }}
        />


        {/* Explore Related Section */}
        {keywords.length > 0 && (
          <div className="explore-related-section">
            {/* Top row: Gemini Chat Component */}

            <GeminiChat ref={chatRef} article={article} initialKeywords={keywords} />

            <div className="explore-content">

              {/* Bottom row: Related Articles with Thumbnails */}
              <div className="related-articles">
                <h4 className="section-subtitle">Suggested Articles</h4>
                <div className="articles-grid">
                  {getRelatedArticles().map((relatedArticle, idx) => (
                    <div
                      key={idx}
                      className="related-article-card"
                      onClick={() => navigate(`/article/${relatedArticle.id}`)}
                    >
                      <div className="article-thumbnail">
                        <img
                          src={relatedArticle.image}
                          alt={relatedArticle.title}
                          onError={(e) => {
                            e.target.src = "/api/placeholder/200/120";
                          }}
                        />
                        <div className="article-overlay">
                          <span className="read-more">Read →</span>
                        </div>
                      </div>
                      <div className="article-info">
                        <h5 className="article-title-small">
                          {relatedArticle.title}
                        </h5>
                        <p className="article-meta-small">
                          {relatedArticle.date}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right side: Keyword Tags */}
              {/* <div className="related-keywords">
                <h4 className="section-subtitle">Related Search</h4>
                <div className="keyword-tags-grid">
                  {keywords.slice(0, 12).map((keyword, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleKeywordClick(keyword)}
                      className="keyword-tag-small"
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div> */}
            </div>
          </div>
        )}
      </div>
      {/* Search Drawer */}
      <SearchDrawer
        open={drawerOpen}
        query={drawerQuery}
        onClose={handleDrawerClose}
      />
      {/* removed 2/3/2026 */}
      {/* {summary && (
        <div className="summary-popover">
          <h4>Summary</h4>
          <p>{summary}</p>
        </div>
      )} */}
    </>
  );
};

export default ArticleWithKeywords;
